<template>
    <div class="space-y-4">
        <div
            v-if="user"
            class="space-y-4"
        >
            <div class="flex items-center gap-2">
                <div class="min-w-10">
                    <Image
                        class="h-10 w-10 mx-auto object-cover rounded-full"
                        :src="user.getProfilePictureUrlWithFallback()"
                    />
                </div>
                <span>
                    {{ user.getFullName() }}
                </span>
            </div>
        </div>
        <div class="space-y-2">
            <template v-if="service">
                <div class="space-y-1">
                    <div class="text-xl font-bold text-gray-800">
                        {{ service.title }}
                    </div>
                    <div class="text-gray-600 text-sm">
                        {{ service.description }}
                    </div>
                </div>
                <div>&nbsp;</div>
                <template v-if="meetingUniqueCode">
                    <div class="flex items-center gap-2">
                        <Icon
                            name="hashtag"
                            class="w-4 h-4 text-gray-500"
                        />
                        <span>
                            {{ meetingUniqueCode }}
                        </span>
                    </div>
                </template>
                <div
                    v-if="enabledLocationTypes"
                    class="flex items-center gap-2"
                >
                    <MeetingLocationList
                        :types="enabledLocationTypes"
                        :meetingLocationIconSize="meetingLocationIconSize"
                        :localizer="localizer"
                    />
                </div>
                <div class="flex items-center gap-2">
                    <Icon
                        name="stopwatch"
                        class="w-4 h-4 text-gray-500"
                    />
                    <span>
                        {{ service.durationInMinutes }}
                        {{
                            localizer.translate(
                                TextTokenEnum.BookingForm_Minutes,
                            )
                        }}
                    </span>
                </div>
            </template>
            <template v-if="price">
                <div class="flex items-center gap-2">
                    <Icon
                        name="dollar-sign"
                        class="w-4 h-4 text-gray-500"
                    />
                    <span>
                        {{ price.getValueAsDecimal() }} {{ price.currency }}
                    </span>
                </div>
            </template>
            <template v-if="numberOfReservedSeats !== null">
                <div class="flex items-center gap-2">
                    <Icon
                        name="users"
                        class="w-4 h-4 text-gray-500"
                    />
                    <span>
                        {{ getReservedSeatsLabel(numberOfReservedSeats) }}
                    </span>
                </div>
            </template>
            <template v-if="recurrenceSettings?.isRecurring()">
                <div class="flex items-center gap-2">
                    <Icon
                        name="arrow-rotate"
                        class="w-4 h-4 text-gray-500"
                    />
                    <span>
                        {{
                            recurrenceSettings.getQuantityDescription(localizer)
                        }}
                    </span>
                </div>
            </template>
            <template v-if="selectedTimeZone">
                <div class="flex items-center gap-2">
                    <Icon
                        name="globe"
                        class="w-3.5 h-3.5 text-gray-500"
                    />
                    <span>
                        {{ selectedTimeZone }}
                    </span>
                </div>
            </template>
            <div v-if="meetingDates && forceShowAllDates">
                <template
                    v-for="(meetingDate, index) in meetingDates"
                    :key="index"
                >
                    <MeetingDateBlock
                        :content="meetingDate.getLocalizedDescription()"
                        :hideIcon="index !== 0"
                    />
                </template>
            </div>
            <div v-else-if="meetingDates">
                <template
                    v-for="(meetingDate, index) in firstMeetingDates"
                    :key="index"
                >
                    <MeetingDateBlock
                        :content="meetingDate.getLocalizedDescription()"
                        :hideIcon="index !== 0"
                    />
                </template>
                <template
                    v-if="
                        recurrenceSettings?.isRecurring() &&
                        remainingMeetingDates.length > 0 &&
                        !isShowingAllDates
                    "
                >
                    <MeetingDateBlock
                        content="..."
                        :hideIcon="true"
                    />
                </template>
                <template
                    v-if="
                        recurrenceSettings?.isRecurring() && isShowingAllDates
                    "
                >
                    <template
                        v-for="(meetingDate, index) in remainingMeetingDates"
                        :key="index"
                    >
                        <MeetingDateBlock
                            :content="meetingDate.getLocalizedDescription()"
                            :hideIcon="true"
                        />
                    </template>
                </template>
                <div
                    v-if="
                        recurrenceSettings?.isRecurring() &&
                        remainingMeetingDates.length > 0
                    "
                    @click="toggleIsShowingAllDates()"
                    class="flex items-center justify-end cursor-pointer gap-1"
                >
                    <template v-if="isShowingAllDates">
                        <Icon
                            name="angle-up"
                            class="w-3 h-3 text-gray-600"
                        />
                        <span class="text-sm">
                            {{
                                localizer.translate(
                                    TextTokenEnum.BookingForm_ShowLessDates,
                                )
                            }}
                        </span>
                    </template>
                    <template v-else>
                        <Icon
                            name="angle-down"
                            class="w-3 h-3 text-gray-600"
                        />
                        <span class="text-sm">
                            {{
                                localizer.translate(
                                    TextTokenEnum.BookingForm_ShowMoreDates,
                                )
                            }}
                        </span>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, type PropType } from 'vue'
    import Icon from '@/Shared/Component/Icon.vue'
    import Company from '@/App/Company/Entity/Company'
    import Service from '@/App/Service/Entity/Service'
    import User from '@/App/User/Entity/User'
    import Money from '@/Shared/Entity/Money'
    import MeetingLocationList from '@/BookingForm/BookingForm/Page/Step/MeetingLocationList.vue'
    import { type MeetingLocationTypeEnum } from '@/Shared/AutoGenerated/Main'
    import RecurringDate from '@/App/Booking/Recurring/Entity/RecurringDate'
    import MeetingDateBlock from '@/BookingForm/BookingForm/Page/Step/MeetingDateBlock.vue'
    import RecurrenceSettings from '@/App/Service/Entity/RecurrenceSettings'
    import LocalizerService from '@/Shared/Service/Localization/LocalizerService'
    import { TextTokenEnum } from '@/Shared/AutoGenerated/LocalizationEnum'
    import Image from '@/Shared/Component/Image.vue'

    const props = defineProps({
        company: {
            type: Object as PropType<Company>,
            required: true,
        },
        service: {
            type: Object as PropType<Service>,
            default: null,
        },
        price: {
            type: Object as PropType<Money>,
            default: null,
        },
        numberOfReservedSeats: {
            type: Number,
            default: null,
        },
        user: {
            type: Object as PropType<User>,
            default: null,
        },
        enabledLocationTypes: {
            type: Array as PropType<MeetingLocationTypeEnum[] | null>,
            required: true,
        },
        selectedTimeZone: {
            type: String,
            default: null,
        },
        meetingUniqueCode: {
            type: String,
            default: null,
        },
        meetingLocationIconSize: {
            type: Number,
            required: true,
        },
        recurrenceSettings: {
            type: Object as PropType<RecurrenceSettings | null>,
            default: null,
        },
        meetingDates: {
            type: Array as PropType<RecurringDate[] | null>,
            default: null,
        },
        forceShowAllDates: {
            type: Boolean,
            default: false,
        },
        localizer: {
            type: Object as PropType<LocalizerService>,
            required: true,
        },
    })

    const MEETINGS_COUNT = 4

    const isShowingAllDates = ref<boolean>(false)
    const firstMeetingDates = props.meetingDates?.slice(0, MEETINGS_COUNT) ?? []
    const remainingMeetingDates =
        props.meetingDates?.slice(MEETINGS_COUNT) ?? []

    const toggleIsShowingAllDates = () => {
        isShowingAllDates.value = !isShowingAllDates.value
    }

    const getReservedSeatsLabel = (numberOfReservedSeats: number): string => {
        if (numberOfReservedSeats === 1) {
            return '1 seat'
        }

        return `${numberOfReservedSeats} seats`
    }
</script>
